import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type GalleryControlsSize = "small" | "large";

export type GalleryControlsProps = {
    currentIndex: number;
    totalCount: number;
    onChange: (newIndex: number) => void;
    size?: GalleryControlsSize;
    showCounter?: boolean;
};

const arrowCss = css({
    flexShrink: 0
});

const containerCss = css({
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: token("space.100")
});

const counterCss = css({
    minWidth: "40px",
    textAlign: "center",
    userSelect: "none"
});

const buttonBaseCss = css({
    "display": "inline-flex",
    "alignItems": "center",
    "justifyContent": "center",
    "border": "none",
    "borderRadius": "100%",
    "cursor": "pointer",
    "flexShrink": 0,
    "flexDirection": "column",
    "backgroundColor": wacToken("color.primary.black"),
    "color": wacToken("color.neutral.light0"),
    "opacity": 1,
    "&:hover": {
        backgroundColor: wacToken("color.secondary.navy")
    },
    "&:active": {
        backgroundColor: wacToken("color.secondary.navy"),
        transform: "scale(0.97)"
    },
    "&:disabled": {
        backgroundColor: wacToken("color.primary.black"),
        opacity: 0.3,
        cursor: "default"
    },
    "&:focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const buttonSizesCss = css({
    ["&.size_small"]: {
        width: "40px",
        height: "40px",
        padding: "13px 10px",
        gap: "10px"
    },
    ["&.size_large"]: {
        width: "60px",
        height: "60px",
        padding: "19.5px 15px",
        gap: "15px"
    }
});

export const GalleryControls = ({
    currentIndex,
    totalCount,
    onChange,
    size = "large",
    showCounter = true
}: GalleryControlsProps) => {
    const sizeClass = `size_${size}`;
    const counterTypographyClass = size === "small" ? "body-sm" : "body-md";
    const isPreviousDisabled = currentIndex <= 1;
    const isNextDisabled = currentIndex >= totalCount;

    const handlePrevious = () => {
        if (!isPreviousDisabled) {
            onChange(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (!isNextDisabled) {
            onChange(currentIndex + 1);
        }
    };

    return (
        <div css={containerCss} role="group" aria-label="Gallery navigation">
            <button
                type="button"
                css={[buttonBaseCss, buttonSizesCss]}
                className={sizeClass}
                onClick={handlePrevious}
                disabled={isPreviousDisabled}
                aria-label="Previous"
                data-testid="gallery-controls-previous"
            >
                <svg
                    css={arrowCss}
                    viewBox="0 0 29 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    style={{ transform: "scaleX(-1)" }}
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.5361 19.1287C19.6575 20.0074 18.2322 20.0074 17.3535 19.1287C16.4748 18.25 16.4748 16.8248 17.3535 15.9461L21.248 12.0525L2.25 12.0525C1.00754 12.0523 0 11.045 0 9.80254C0.000234604 8.56023 1.00769 7.55276 2.25 7.55254L21.0654 7.55254L17.3535 3.84063C16.475 2.96202 16.4751 1.53765 17.3535 0.658987C18.2322 -0.219692 19.6575 -0.219692 20.5361 0.658987L27.8291 7.95293C28.4154 8.35896 28.8006 9.03537 28.8008 9.80254C28.8008 10.2822 28.6493 10.7265 28.3936 11.0916C28.2867 11.3125 28.1433 11.5206 27.96 11.7039L20.5361 19.1287Z"
                        fill="currentColor"
                    />
                </svg>
            </button>

            {showCounter && (
                <span
                    css={counterCss}
                    className={counterTypographyClass}
                    aria-live="polite"
                    aria-atomic="true"
                    data-testid="gallery-controls-counter"
                >
                    {currentIndex}/{totalCount}
                </span>
            )}

            <button
                type="button"
                css={[buttonBaseCss, buttonSizesCss]}
                className={sizeClass}
                onClick={handleNext}
                disabled={isNextDisabled}
                aria-label="Next"
                data-testid="gallery-controls-next"
            >
                <svg
                    css={arrowCss}
                    viewBox="0 0 29 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.5361 19.1287C19.6575 20.0074 18.2322 20.0074 17.3535 19.1287C16.4748 18.25 16.4748 16.8248 17.3535 15.9461L21.248 12.0525L2.25 12.0525C1.00754 12.0523 0 11.045 0 9.80254C0.000234604 8.56023 1.00769 7.55276 2.25 7.55254L21.0654 7.55254L17.3535 3.84063C16.475 2.96202 16.4751 1.53765 17.3535 0.658987C18.2322 -0.219692 19.6575 -0.219692 20.5361 0.658987L27.8291 7.95293C28.4154 8.35896 28.8006 9.03537 28.8008 9.80254C28.8008 10.2822 28.6493 10.7265 28.3936 11.0916C28.2867 11.3125 28.1433 11.5206 27.96 11.7039L20.5361 19.1287Z"
                        fill="currentColor"
                    />
                </svg>
            </button>
        </div>
    );
};
